import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  CSSProperties
} from 'react';
import Store from 'store/store';
const { selectors } = Store;
import {
  createTheme,
  darken,
  lighten,
  ThemeProvider as MuiThemeProvider
} from '@material-ui/core/styles';
import api from 'api';
import { greenBackground, greenBackgroundDark, greenText } from 'utils/colors';
import { useAuth } from 'providers/AuthProvider/SessionAuthProvider';
import { paletteSettings } from 'components/Settings/Palette/utils';
import { connect } from 'react-redux';
interface ThemeContextProps {
  updateColors: Function;
}

const defaultColors = ['#2c5f9e', '#1b2d5a'];
const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  riskProfiler: any;
}

const ThemeProvider = ({ children, riskProfiler }: ThemeProviderProps) => {
  const isAuthenticated = useAuth();
  const palettes = paletteSettings(undefined, riskProfiler);

  // Helper to initialize state and store both state and setter, with fallback to defaultColors
  const initializePalettes = (
    config: { key: string; default?: string[] }[],
    fallback: string[] = defaultColors
  ) => {
    return config.reduce(
      (acc, { key, default: defaultValue }) => {
        // Use the default value from config or the global fallback
        const initialValue = defaultValue || fallback;
        const [state, setter] = useState<string[]>(initialValue);
        acc[key] = { state, setter, default: initialValue };
        return acc;
      },
      {} as {
        [key: string]: {
          state: string[];
          setter: React.Dispatch<React.SetStateAction<string[]>>;
          default: string[];
        };
      }
    );
  };

  // Initialize palettes dynamically, fallback to defaultColors if no config found
  const paletteMappings = initializePalettes(palettes);

  const updateColors = (): Promise<any> => {
    return api.getAllPalettes().then((response: any) => {
      const palettes = response.data.results;
      const processedKeys = new Set<string>();

      // Iterate over each palette in the response
      palettes.forEach((palette: any) => {
        const paletteMapping = paletteMappings[palette.section_key];

        if (paletteMapping) {
          if (palette.colors.length > 0) {
            const newColors = [...paletteMapping.state];
            palette.colors.forEach((color: any) => {
              newColors[color.index] = color.hex_code;
            });
            paletteMapping.setter(newColors);
          } else {
            // Reset to default if no colors are found
            paletteMapping.setter([...paletteMapping.default]);
          }
          processedKeys.add(palette.section_key);
        }
      });

      // Set default colors for any palette mappings not processed
      Object.keys(paletteMappings).forEach(key => {
        if (!processedKeys.has(key)) {
          const paletteMapping = paletteMappings[key];
          paletteMapping.setter([...paletteMapping.default]);
        }
      });

      // Return the response after processing all palettes
      return response;
    });
  };

  // Helper to build charts object dynamically
  const getChartPalettes = (paletteKeys: string[]) => {
    const charts: { [key: string]: string[] } = {};

    paletteKeys.forEach((key: any) => {
      const { state } = paletteMappings[key] || paletteMappings['default'];
      charts[key] = state;
    });

    return charts;
  };

  useEffect(() => {
    if (isAuthenticated.isAuthenticated) {
      updateColors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated.isAuthenticated) {
      updateColors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const fontPrimary = '0.8rem';
  const fontPrimaryTitle = '1.125rem';
  const fontInput = '0.8rem';
  const fontPrimarySub = '0.7rem';

  const finPalette = useMemo(() => {
    const { state: colors } = paletteMappings['theme'] || {
      state: defaultColors
    };

    // Dynamically build the charts object by fetching palettes based on the keys from paletteMappings
    const charts = getChartPalettes(
      Object.keys(paletteMappings).filter(key => key !== 'theme')
    );

    return {
      baseColors: {
        tableGrey: '#f9f9f9',
        primary: (colors && colors[0]) || '#2c5f9e',
        secondary: (colors && colors[1]) || '#1b2d5a',
        charts: charts
      }
    };
  }, [paletteMappings]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: finPalette.baseColors.primary
          },
          secondary: {
            main: finPalette.baseColors.secondary,
            light: '#aed500'
          },
          error: {
            dark: '#d95045',
            main: '#EC6A22',
            light: '#f3b600'
          },
          ...finPalette
        },
        typography: {
          fontFamily: 'Gilroy, Roboto',
          h6: {
            fontWeight: 400
          }
        },
        overrides: {
          MuiSelect: {
            select: {
              '& focus': {
                background: 'none'
              }
            },
            selectMenu: {
              fontSize: fontInput
            }
          },
          MuiButton: {
            containedSecondary: {
              backgroundColor: finPalette.baseColors.primary,
              '&:hover': {
                backgroundColor: darken(finPalette.baseColors.primary, 0.2)
              }
            },
            containedPrimary: {
              backgroundColor: finPalette.baseColors.secondary,
              '&:hover': {
                backgroundColor: lighten(finPalette.baseColors.secondary, 0.2)
              }
            },
            label: {
              color: '#fff',
              width: '100%',
              display: 'inherit',
              alignItems: 'inherit',
              lineHeight: '1',
              justifyContent: 'inherit',
              fontSize: fontInput
            },
            root: {
              borderRadius: '25px',
              padding: '13px 25px',
              fontSize: fontPrimary,
              boxShadow: 'none !important'
            },
            endIcon: {
              marginLeft: '0',
              marginRight: '0'
            }
          },
          MuiButtonGroup: {
            root: {
              borderRadius: '25px',
              overflow: 'hidden'
            }
          },
          MuiTable: {
            root: {
              fontFamily: 'Gilroy',
              borderRadius: '25px',
              overflow: 'hidden'
            }
          },
          MuiTableCell: {
            root: {
              fontFamily: 'Gilroy'
            },
            body: {
              fontWeight: 'lighter',
              fontSize: fontInput
            },
            head: {
              color: '#747474',
              fontWeight: 'lighter',
              fontSize: fontInput
            }
          },
          MuiInputBase: {
            root: {
              fontWeight: 500,
              fontSize: fontPrimarySub
            },
            input: {
              fontSize: fontInput
            },
            inputMultiline: {
              fontSize: '0.83rem',
              lineHeight: 1.35
            }
          },
          MuiOutlinedInput: {
            root: {
              borderRadius: '25px'
            }
          },
          MuiGrid: {
            item: {
              fontSize: fontInput
            }
          },
          MuiInputLabel: {
            root: {
              fontWeight: 500
            },
            shrink: {
              fontSize: '1.1rem' // TODO TO DISCUSS
            },
            formControl: {
              width: '340px',
              fontSize: fontInput
            }
          },
          MuiFormHelperText: {
            root: {
              position: 'absolute',
              bottom: '-1rem'
            }
          },
          MuiInputAdornment: {
            positionEnd: {
              fontSize: fontPrimary
            }
          },
          MuiMenuItem: {
            root: {
              fontSize: fontPrimary
            }
          },
          MuiTypography: {
            h5: {
              fontSize: fontPrimaryTitle,
              fontWeight: 400
            },
            h6: {
              fontSize: fontPrimaryTitle,
              fontWeight: 400
            }
          },
          MuiPaper: {
            rounded: {
              borderRadius: '25px'
            }
          },
          MuiTooltip: {
            tooltip: {
              fontSize: '13px',
              borderRadius: '15px',
              padding: '0.5rem'
            }
          },
          MuiChip: {
            deletableColorPrimary: {
              color: greenText,
              backgroundColor: greenBackground,
              '&:hover': {
                backgroundColor: greenBackgroundDark
              },
              '&:focus': {
                backgroundColor: greenBackgroundDark
              }
            },
            deleteIconColorPrimary: {
              color: 'rgba(56, 142, 60, 0.4)',
              '&:hover': {
                color: 'rgba(56, 142, 60, 0.6)'
              }
            }
          }
        }
      }),
    [finPalette]
  );

  return (
    <ThemeContext.Provider value={{ updateColors }}>
      <MuiThemeProvider theme={theme}>
        <div
          style={
            {
              '--primary-color': finPalette.baseColors.primary,
              '--secondary-color': finPalette.baseColors.secondary
            } as CSSProperties
          }
        >
          {children}
        </div>
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default connect((state: any) => ({
  riskProfiler: selectors.firmSelectors.riskProfiler(state)
}))(ThemeProvider);
